export type TimelineStopType = 'pickup' | 'via' | 'delivery'
export type TimelineStopStatus = 'completed' | 'at_stop' | 'in_transit' | 'upcoming'

export type TimelineViaInput = {
  location?: string | null
  actualArrivalTime?: string | Date | null
  actualDepartureTime?: string | Date | null
  planedArrivalTime?: string | Date | null
  code?: number | null
  Location?: string | null
  ActualArrivalTime?: string | Date | null
  ActualDepartureTime?: string | Date | null
  PlanedArrivalTime?: string | Date | null
  Code?: number | null
}

export type TimelineVehicleInput = {
  vehicleNumber?: string | null
  pickupLocation?: string | null
  deliveryLocation?: string | null
  atPickup?: string | Date | null
  dtPickup?: string | Date | null
  atDelivery?: string | Date | null
  dtDelivery?: string | Date | null
  viaLocationList?: TimelineViaInput[] | null
  PickupLocation?: string | null
  DeliveryLocation?: string | null
  ATPickup?: string | Date | null
  DTPickup?: string | Date | null
  ATDelivery?: string | Date | null
  DTDelivery?: string | Date | null
  ViaLocationList?: TimelineViaInput[] | null
}

export type TimelineStop = {
  id: string
  type: TimelineStopType
  typeLabel: string
  name: string
  arrival: string | Date | null
  departure: string | Date | null
  status: TimelineStopStatus
}

export type JobTimelineModel = {
  stops: TimelineStop[]
  activeStopIndex: number
  /** 0 = start of segment toward stop, 0.5 = mid transit, 1 = at stop */
  vehicleProgress: number
  vehicleNumber: string
}

function hasTime(value: string | Date | null | undefined): boolean {
  if (value == null || value === '') return false
  const d = value instanceof Date ? value : new Date(value)
  return !Number.isNaN(d.getTime())
}

function pickTime(...values: Array<string | Date | null | undefined>): string | Date | null {
  for (const v of values) {
    if (hasTime(v)) return v as string | Date
  }
  return null
}

function sortVias(vias: TimelineViaInput[]): TimelineViaInput[] {
  return [...vias].sort((a, b) => {
    const aPlan = a.planedArrivalTime ?? a.PlanedArrivalTime
    const bPlan = b.planedArrivalTime ?? b.PlanedArrivalTime
    const aTime = hasTime(aPlan) ? new Date(aPlan as any).getTime() : Number.MAX_SAFE_INTEGER
    const bTime = hasTime(bPlan) ? new Date(bPlan as any).getTime() : Number.MAX_SAFE_INTEGER
    if (aTime !== bTime) return aTime - bTime
    return (a.code ?? a.Code ?? 0) - (b.code ?? b.Code ?? 0)
  })
}

export function buildJobTimeline(
  vehicle: TimelineVehicleInput | null | undefined
): JobTimelineModel | null {
  if (!vehicle) return null

  const viasRaw = vehicle.viaLocationList ?? vehicle.ViaLocationList ?? []
  const vias = sortVias(Array.isArray(viasRaw) ? viasRaw : [])

  const draft = [
    {
      id: 'pickup',
      type: 'pickup' as const,
      typeLabel: 'Pickup',
      name: vehicle.pickupLocation ?? vehicle.PickupLocation ?? 'Pickup',
      arrival: pickTime(vehicle.atPickup, vehicle.ATPickup),
      departure: pickTime(vehicle.dtPickup, vehicle.DTPickup),
    },
    ...vias.map((via, index) => ({
      id: `via-${via.code ?? via.Code ?? index}`,
      type: 'via' as const,
      typeLabel: 'Via',
      name: via.location ?? via.Location ?? `Via ${index + 1}`,
      arrival: pickTime(via.actualArrivalTime, via.ActualArrivalTime),
      departure: pickTime(via.actualDepartureTime, via.ActualDepartureTime),
    })),
    {
      id: 'delivery',
      type: 'delivery' as const,
      typeLabel: 'Delivery',
      name: vehicle.deliveryLocation ?? vehicle.DeliveryLocation ?? 'Delivery',
      arrival: pickTime(vehicle.atDelivery, vehicle.ATDelivery),
      departure: pickTime(vehicle.dtDelivery, vehicle.DTDelivery),
    },
  ]

  const stops: TimelineStop[] = []
  let previousCompleted = false

  for (let i = 0; i < draft.length; i++) {
    const stop = draft[i]
    const hasIn = hasTime(stop.arrival)
    const hasOut = hasTime(stop.departure)
    let status: TimelineStopStatus

    if (hasIn && hasOut) {
      status = 'completed'
    } else if (hasIn && !hasOut) {
      status = 'at_stop'
    } else if (i > 0 && previousCompleted && !hasIn) {
      status = 'in_transit'
    } else {
      status = 'upcoming'
    }

    stops.push({...stop, status})
    previousCompleted = status === 'completed'
  }

  const atStopIndex = stops.findIndex((s) => s.status === 'at_stop')
  const inTransitIndex = stops.findIndex((s) => s.status === 'in_transit')
  const allCompleted = stops.length > 0 && stops.every((s) => s.status === 'completed')

  let activeStopIndex = 0
  let vehicleProgress = 0

  if (allCompleted) {
    activeStopIndex = stops.length - 1
    vehicleProgress = 1
  } else if (atStopIndex >= 0) {
    activeStopIndex = atStopIndex
    vehicleProgress = 1
  } else if (inTransitIndex >= 0) {
    activeStopIndex = inTransitIndex
    vehicleProgress = 0.5
  } else {
    activeStopIndex = 0
    vehicleProgress = 0
  }

  return {
    stops,
    activeStopIndex,
    vehicleProgress,
    vehicleNumber: vehicle.vehicleNumber ?? '',
  }
}

export function segmentStatus(
  from: TimelineStop,
  to: TimelineStop
): 'completed' | 'in_transit' | 'upcoming' {
  if (from.status === 'completed' && (to.status === 'completed' || to.status === 'at_stop')) {
    return 'completed'
  }
  if (to.status === 'in_transit') return 'in_transit'
  return 'upcoming'
}
